import { FormEvent, KeyboardEvent, useState } from "react";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Grid,
  MenuItem,
  Select,
  FormControl,
  Tooltip,
} from "@mui/material";
import { toast } from "react-hot-toast";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBack from "@mui/icons-material/ArrowBack";
import IconButton from "@mui/material/IconButton";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createLead } from "../features/lead-module/leadAction";

const StyledBackButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(2),
  right: theme.spacing(2),
  backgroundColor: "#f2efe9",
  color: "#455354",
  transition: "background 0.24s ease-in-out, color 0.24s ease-in-out",
  "&:hover": {
    background: "linear-gradient(to bottom, #758d8e, #4b5b5d)",
    color: "#f2efe9",
  },
}));

const Container = styled(Box)(({ theme }) => ({
  minHeight: "95vh",
  background: "#fff",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(2),
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(2),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(3),
  },
}));

const Content = styled(Box)<{ component?: React.ElementType }>(({ theme }) => ({
  width: "100%",
  maxWidth: 800,
  height: "auto",
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    maxWidth: "100%",
    marginTop: theme.spacing(2),
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "5px",
    backgroundColor: "#f5f5f5",
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#bdbdbd",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#4b5b5d",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#757575",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#4b5b5d",
  },
  "& .MuiOutlinedInput-input": {
    padding: "10px 12px",
    fontSize: "0.85rem",
    [theme.breakpoints.down("sm")]: {
      padding: "10px 12px",
      fontSize: "0.8rem",
    },
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  width: "100%",
  borderRadius: "5px",
  backgroundColor: "#f5f5f5",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e0e0e0",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#bdbdbd",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#4b5b5d",
  },
  "& .MuiSelect-select": {
    padding: "10px 12px",
    fontSize: "0.85rem",
    [theme.breakpoints.down("sm")]: {
      padding: "10px 12px",
      fontSize: "0.8rem",
    },
  },
  height: "40px",
  [theme.breakpoints.down("sm")]: {
    height: "36px",
  },
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontSize: "45px",
  fontWeight: 600,
  color: "#4b5b5d",
  textAlign: "center",
  [theme.breakpoints.down("sm")]: {
    fontSize: "30px",
  },
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  color: "#757575",
  fontSize: 16,
  marginBottom: theme.spacing(3),
  textAlign: "center",
  alignSelf: "center",
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
    marginBottom: theme.spacing(2),
  },
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  color: "#455354",
  fontSize: "0.95rem",
  fontWeight: 600,
  marginBottom: theme.spacing(0.5),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.8rem",
  },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  width: "100%",
  textTransform: "none",
  fontWeight: 600,
  background: "linear-gradient(to top, #758d8e, #4b5b5d)",
  color: "#fff",
  borderRadius: "6px",
  padding: theme.spacing(1),
  fontSize: "12px",
  "&:hover": {
    background: "linear-gradient(to top, #758d8e, #4b5b5d)",
  },
  "&:disabled": {
    background: "#bdbdbd",
    color: "#fff",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "10px",
    minHeight: "37px",
  },
}));

const CustomLead = () => {
  const [formData, setFormData] = useState({
    fullname: "",
    email: "",
    companyName: "",
    phoneNumber: "",
    teamSize: "",
    description: "",
  });
  const [errors, setErrors] = useState({
    fullname: "",
    email: "",
    companyName: "",
    phoneNumber: "",
    teamSize: "",
    description: "",
  });
  const [touched, setTouched] = useState({
    fullname: false,
    email: false,
    companyName: false,
    phoneNumber: false,
    teamSize: false,
    description: false,
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const validateField = (name: string, value: string) => {
    switch (name) {
      case "fullname":
        if (!value) return "Full name is required";
        return "";
      case "email":
        if (!value) return "Email Address is required";
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(value)) return "Enter a valid email address";
        return "";
      case "companyName":
        if (!value) return "Company name is required";
        return "";
      case "phoneNumber":
        if (!value) return "Phone number is required";
        const phoneRegex = /^\d{10}$/;
        if (!phoneRegex.test(value))
          return "Phone number must be exactly 10 digits with no other characters";
        return "";
      case "teamSize":
        if (!value) return "Team size is required";
        return "";
      case "description":
        if (!value) return "Description is required";
        if (value.length < 10)
          return "Description must be at least 10 characters";
        return "";
      default:
        return "";
    }
  };

  // Function to handle back navigation to the previous route
  const handleBack = () => {
    setFormData({
      fullname: "",
      email: "",
      companyName: "",
      phoneNumber: "",
      teamSize: "",
      description: "",
    });
    setTouched({
      fullname: false,
      email: false,
      companyName: false,
      phoneNumber: false,
      teamSize: false,
      description: false,
    });
    setErrors({
      fullname: "",
      email: "",
      companyName: "",
      phoneNumber: "",
      teamSize: "",
      description: "",
    });
    navigate(-1);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      if (touched[name as keyof typeof touched]) {
        setErrors((prev) => ({
          ...prev,
          [name]: validateField(name, value as string),
        }));
      }
    }
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target as { name: string; value: string };
    if (name) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      if (touched[name as keyof typeof touched]) {
        setErrors((prev) => ({
          ...prev,
          [name]: validateField(name, value as string),
        }));
      }
    }
  };

  const handleBlur = (e: React.FocusEvent<{ name: string }>) => {
    const { name } = e.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, formData[name as keyof typeof formData]),
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const newErrors = {
      fullname: validateField("fullname", formData.fullname),
      email: validateField("email", formData.email),
      companyName: validateField("companyName", formData.companyName),
      phoneNumber: validateField("phoneNumber", formData.phoneNumber),
      teamSize: validateField("teamSize", formData.teamSize),
      description: validateField("description", formData.description),
    };

    setErrors(newErrors);
    setTouched({
      fullname: true,
      email: true,
      companyName: true,
      phoneNumber: true,
      teamSize: true,
      description: true,
    });

    const hasError = Object.values(newErrors).some((msg) => msg);
    if (!hasError) {
      try {
        const result = await dispatch(createLead(formData) as any).unwrap();
        if (result.success) {
          setFormData({
            fullname: "",
            email: "",
            companyName: "",
            phoneNumber: "",
            teamSize: "",
            description: "",
          });
          setTouched({
            fullname: false,
            email: false,
            companyName: false,
            phoneNumber: false,
            teamSize: false,
            description: false,
          });
          navigate(-1);
        }
      } catch (error) {
        toast.error("Failed to create lead. Please try again.");
      }
    } else {
      toast.error("Please fix the errors in the form.");
    }
    setLoading(false);
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmit(e as any);
    }
  };

  const isFormValid =
    Boolean(formData.fullname) &&
    validateField("email", formData.email) === "" &&
    Boolean(formData.companyName) &&
    validateField("phoneNumber", formData.phoneNumber) === "" &&
    Boolean(formData.teamSize) &&
    formData.description.length >= 10;

  return (
    <Container>
      <Tooltip title="Back">
        <StyledBackButton aria-label="back" onClick={handleBack}>
          <ArrowBack />
        </StyledBackButton>
      </Tooltip>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Content
          component="form"
          onSubmit={handleSubmit}
          onKeyDown={handleKeyPress}
        >
          <WelcomeText>Let’s Build Your Enterprise Plan</WelcomeText>
          <Subtitle>
            Tell us what your team needs, and we'll tailor the perfect plan.
          </Subtitle>

          <Grid container spacing={2} sx={{ width: "100%", mb: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FieldLabel>Fullname</FieldLabel>
              <StyledTextField
                autoFocus
                name="fullname"
                variant="outlined"
                type="text"
                value={formData.fullname}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.fullname}
                helperText={errors.fullname}
                required
                size={isMobile ? "small" : "medium"}
                sx={{ mb: 0 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FieldLabel>Email Address</FieldLabel>
              <StyledTextField
                name="email"
                variant="outlined"
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.email}
                helperText={errors.email}
                required
                size={isMobile ? "small" : "medium"}
                sx={{ mb: 0 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FieldLabel>Company Name</FieldLabel>
              <StyledTextField
                name="companyName"
                variant="outlined"
                type="text"
                value={formData.companyName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.companyName}
                helperText={errors.companyName}
                required
                size={isMobile ? "small" : "medium"}
                sx={{ mb: 0 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FieldLabel>Phone Number</FieldLabel>
              <StyledTextField
                name="phoneNumber"
                variant="outlined"
                type="tel"
                value={formData.phoneNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
                required
                size={isMobile ? "small" : "medium"}
                sx={{ mb: 0 }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth sx={{ mb: 0 }}>
                <FieldLabel>Team Size</FieldLabel>
                <StyledSelect
                  labelId="team-size-label"
                  name="teamSize"
                  value={formData.teamSize}
                  onChange={handleSelectChange}
                  onBlur={handleBlur}
                  error={!!errors.teamSize}
                  required
                  size={isMobile ? "small" : "medium"}
                >
                  <MenuItem value="1-100">1-100</MenuItem>
                  <MenuItem value="101-500">101-500</MenuItem>
                  <MenuItem value="501-1000">501-1000</MenuItem>
                  <MenuItem value="1000+">1000+</MenuItem>
                </StyledSelect>
                {errors.teamSize && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ mt: 0.5, ml: 1.5 }}
                  >
                    {errors.teamSize}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FieldLabel>Descriptiom</FieldLabel>
              <StyledTextField
                name="description"
                variant="outlined"
                placeholder="Describe your requirements, integrations needed etc."
                multiline
                rows={1}
                value={formData.description}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.description}
                helperText={errors.description}
                required
                size={isMobile ? "small" : "medium"}
                sx={{ mb: 0 }}
              />
            </Grid>
          </Grid>

          <Grid
            container
            spacing={2}
            sx={{ width: "100%", justifyContent: "center" }}
          >
            <Grid
              size={{ xs: 12, sm: 6 }}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <motion.div
                whileHover={{
                  scale: 1.04,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                }}
                whileTap={{ scale: 0.98 }}
                style={{ width: "100%", maxWidth: "300px" }}
              >
                <PrimaryButton
                  type="submit"
                  variant="contained"
                  disabled={loading || !isFormValid}
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                >
                  {loading ? "Submitting..." : "Request Enterprise Plan"}
                </PrimaryButton>
              </motion.div>
            </Grid>
          </Grid>
        </Content>
      </motion.div>
    </Container>
  );
};

export default CustomLead;
